/**
 * Zugriff auf die Schnittstelle des Servers.
 *
 * Alle Aufrufe laufen über das Sitzungscookie. Fehler werden als ApiError mit
 * einer lesbaren deutschen Meldung weitergereicht, damit die Oberfläche sie
 * direkt anzeigen kann.
 */

/** Ein Fehler aus der Schnittstelle. */
export class ApiError extends Error {
  readonly status: number;
  /** Vollständige Antwort des Servers, zum Beispiel die aktuelle Fassung bei einem Konflikt. */
  readonly data: unknown;

  constructor(status: number, message: string, data: unknown = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

/** Kein Netz oder Server nicht erreichbar? */
export function isOffline(error: unknown): boolean {
  return error instanceof ApiError && error.status === 0;
}

/** Führt einen Aufruf aus und liefert die Antwort als Objekt. */
async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  extraHeaders: Record<string, string> = {},
): Promise<T> {
  const headers: Record<string, string> = { ...extraHeaders };
  if (body !== undefined) {
    headers['content-type'] = 'application/json';
  }

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'Keine Verbindung zum Server. Bitte später erneut versuchen.');
  }

  const text = await response.text();
  let data: unknown = null;
  if (text !== '') {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }

  if (!response.ok) {
    const raw = data as { error?: unknown } | null;
    const message =
      raw !== null && typeof raw.error === 'string'
        ? raw.error
        : `Der Server hat mit ${response.status} geantwortet.`;
    throw new ApiError(response.status, message, data);
  }

  return data as T;
}

/** Die Schnittstelle des Kochbuchs. */
export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown, headers?: Record<string, string>) =>
    request<T>('POST', path, body, headers),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
  /** Lädt eine Datei als Formular hoch (Feld „file“). */
  upload: async <T>(path: string, file: File): Promise<T> => {
    const form = new FormData();
    form.append('file', file);
    let response: Response;
    try {
      response = await fetch(path, { method: 'POST', body: form });
    } catch {
      throw new ApiError(0, 'Keine Verbindung zum Server. Bitte später erneut versuchen.');
    }
    const data = (await response.json().catch(() => null)) as (T & { error?: string }) | null;
    if (!response.ok || data === null) {
      throw new ApiError(
        response.status,
        data?.error ?? `Der Server hat mit ${response.status} geantwortet.`,
      );
    }
    return data;
  },
};
